'use client'

import React, { createContext, useContext, useMemo, useState } from 'react'

export const SidebarContext = createContext<SidebarContextType | null>(null)

type SidebarState = Record<
  string,
  {
    open: boolean
    path: string
  }
>

type SidebarStateHistory = Record<string, string[]>

type SidebarTriggerObserver = Record<string, any>

type SidebarContextType = {
  open: (id: string, path: string) => void
  close: (id: string) => void
  isOpen: (id: string) => boolean
  path: (id: string) => string
  goForward: (id: string, path: string) => void
  goBackwards: (id: string) => void
  currentHistory: (id: string) => string[]
}

const SidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<SidebarState>({})
  const [history, setHistory] = useState<SidebarStateHistory>({})

  const open = (id: string, path: string) => {
    setState((s) => ({ ...s, [id]: { open: true, path } }))
    setHistory((h) => ({ ...h, [id]: [...(h[id] || []), path] }))
  }

  const close = (id: string) => {
    setState((s) => ({ ...s, [id]: { ...s[id], open: false } }))
    setHistory((h) => ({ ...h, [id]: [] }))
  }

  const isOpen = (id: string) => state[id]?.open

  const goForward = (id: string, path: string) => {
    setState((s) => ({ ...s, [id]: { ...s[id], path } }))
    setHistory((h) => ({ ...h, [id]: [...(h[id] || []), path] }))
  }

  const goBackwards = (id: string) => {
    setHistory((h) => {
      const newHistory = [...(h[id] || [])]
      newHistory.pop()
      const previousPath = newHistory.at(-1) || ''
      setState((s) => ({ ...s, [id]: { ...s[id], path: previousPath } }))
      return { ...h, [id]: newHistory }
    })
  }

  const currentHistory = (id: string) => history[id] || []

  const path = (id: string) => state[id]?.path ?? undefined

  const props = useMemo(
    () => ({ open, close, isOpen, path, goForward, goBackwards, currentHistory }),
    [open, close, isOpen, path, goForward, goBackwards, currentHistory],
  )

  return <SidebarContext.Provider value={props}>{children}</SidebarContext.Provider>
}

export default SidebarProvider

export function useSidebar() {
  const ctx = useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar must be used inside SidebarProvider')
  return ctx
}
